import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ClockInData, CurrentTrip, StockProductItem } from './model';
import { RootState } from '../root/root-types';
import { tripApi } from './api';
import moment from 'moment';
import { cloneDeep, isNull } from 'lodash';
import { Asset, CategoryFilterItem, Promotion } from '../products';

export const TRIP_FEATURE_KEY = 'trip';

export interface TripState {
  ongoingTrip: CurrentTrip;
  todayClockInData: ClockInData;
  showStockTootTip: boolean;
  stockListWithQuantity: StockProductItem[];
}

export const initialTripState: TripState = {
  ongoingTrip: {
    tripId: null,
    visitId: null,
    stock: [],
  },
  todayClockInData: {
    totalWorkingHours: {
      hours: 0,
      minutes: 0,
    },
  },
  showStockTootTip: true,
  stockListWithQuantity: [],
};

export const tripSlice = createSlice({
  name: TRIP_FEATURE_KEY,
  initialState: initialTripState,
  reducers: {
    updateOngoingTrip(state, action: PayloadAction<Partial<CurrentTrip>>) {
      const existingTrip = state.ongoingTrip;
      const updatedTrip = action.payload;
      state.ongoingTrip = { ...existingTrip, ...updatedTrip };
    },
    updateTodayClockInData(state, action: PayloadAction<ClockInData>) {
      state.todayClockInData = action.payload;
    },
    updateShowStockToolTip(state, action: PayloadAction<boolean>) {
      state.showStockTootTip = action.payload;
    },
    updateStockWithQuantity: (
      state,
      action: PayloadAction<{
        productVariantId: number;
        quantity: string;
        name: string;
        assets: Asset[];
        promotions: Promotion[];
        categories: CategoryFilterItem[];
        enabled: boolean;
        price: number;
      }>
    ) => {
      const {
        productVariantId,
        quantity,
        name,
        assets,
        promotions,
        categories,
        enabled,
        price,
      } = action.payload;

      if (quantity.trim() === '') {
        state.stockListWithQuantity = state.stockListWithQuantity.filter(
          (item) => item.variantProductId !== productVariantId
        );
      } else {
        const existingProduct = state.stockListWithQuantity.find(
          (item) => item.variantProductId === productVariantId
        );

        if (existingProduct) {
          existingProduct.quantity = Number(quantity);
        } else {
          state.stockListWithQuantity.push({
            variantProductId: productVariantId,
            quantity: Number(quantity),
            productName: name,
            assets,
            categories,
            promotions,
            enabled,
            price,
          });
        }
      }
    },
    clearStockWithQuantity: (state) => {
      state.stockListWithQuantity = [];
    },
  },
  extraReducers(builder) {
    builder.addMatcher(
      tripApi.endpoints.tripClockInHistory.matchFulfilled,
      (state, action) => {
        const isActive = action.meta.arg.originalArgs.isActive;
        if (isActive) {
          const activeTripData = action.payload.records?.[0]?.dayActivity?.[0];
          if (activeTripData) {
            const tripId = activeTripData?.tripId;
            const clockIn = activeTripData?.clockIn;
            const clockOut = activeTripData?.clockOut;
            const payload: ClockInData = {
              lastClockIn: clockIn,
              clockIn,
              clockOut,
              totalWorkingHours: { hours: 0, minutes: 0 },
            };

            tripSlice.caseReducers.updateOngoingTrip(state, {
              ...action,
              payload: { tripId },
            });
            tripSlice.caseReducers.updateTodayClockInData(state, {
              ...action,
              payload: payload,
            });
          }
          return;
        }

        if (action.meta.arg.originalArgs.isToday) {
          const foundTodayHistory = action.payload.records.find(
            (history) => history?.date === moment().format('YYYY-MM-DD')
          );

          if (foundTodayHistory) {
            const clonedTodayHistory = cloneDeep(foundTodayHistory);
            // sort so that latest clockin time will come first
            const clockInSorted = clonedTodayHistory?.dayActivity?.sort(
              (a, b) => moment(b.clockIn).diff(moment(a.clockIn))
            );
            // agar h to take first one;
            const latestTripData = clockInSorted?.[0];
            const clockIn = latestTripData?.clockIn;
            const lastClockIn = clockInSorted?.at(-1)?.clockIn;
            const clockOut = latestTripData?.clockOut;
            const tripId = latestTripData?.tripId;

            tripSlice.caseReducers.updateOngoingTrip(state, {
              ...action,
              payload: { tripId: isNull(clockOut) ? tripId : null },
            });

            const totalWorkingHours = clonedTodayHistory?.totalWorkingHours || {
              hours: 0,
              minutes: 0,
            };

            const payload: ClockInData = {
              lastClockIn,
              clockIn,
              clockOut,
              totalWorkingHours,
            };

            const updatedAction = {
              ...action,
              payload,
            };
            tripSlice.caseReducers.updateTodayClockInData(state, updatedAction);
          } else {
            tripSlice.caseReducers.updateOngoingTrip(state, {
              ...action,
              payload: { tripId: null, visitId: null, stock: [] },
            });
          }
        }
      }
    );
    builder.addMatcher(
      tripApi.endpoints.tripStockList.matchFulfilled,
      (state, action) => {
        const tripStockList = action.payload;
        tripSlice.caseReducers.updateOngoingTrip(state, {
          ...action,
          payload: { stock: tripStockList || [] },
        });
      }
    );
    builder.addMatcher(
      tripApi.endpoints.tripCheckInHistory.matchFulfilled,
      (state, action) => {
        const isActive = action.meta.arg.originalArgs.isActive;
        const visits = action.payload.visits;

        if (isActive) {
          if (visits?.length) {
            const activeVisit = visits[0];
            const customerDetails = activeVisit?.customerDetails;
            tripSlice.caseReducers.updateOngoingTrip(state, {
              ...action,
              payload: {
                visitId: activeVisit?.id || null,
                tripId: activeVisit?.tripId || null,
                activeCustomer: {
                  id: customerDetails.id,
                  name: customerDetails?.companyName,
                },
              },
            });
          } else {
            tripSlice.caseReducers.updateOngoingTrip(state, {
              ...action,
              payload: {
                visitId: null,
                activeCustomer: undefined,
              },
            });
          }
        }
      }
    );
  },
});

export const tripReducers = tripSlice.reducer;

export const {
  updateOngoingTrip,
  updateTodayClockInData,
  updateShowStockToolTip,
  updateStockWithQuantity,
  clearStockWithQuantity,
} = tripSlice.actions;

export const getTripState = (rootState: RootState): TripState =>
  rootState[TRIP_FEATURE_KEY];

export const getCurrentTrip = createSelector(
  getTripState,
  (state) => state.ongoingTrip
);

export const getCurrentTripId = createSelector(
  getTripState,
  (state) => state.ongoingTrip.tripId
);

export const getActiveVisitId = createSelector(
  getTripState,
  (state) => state.ongoingTrip.visitId
);

export const getActiveVisitCustomer = createSelector(
  getTripState,
  (state) => state.ongoingTrip.activeCustomer
);

export const getTripStockList = createSelector(
  getTripState,
  (state) => state.ongoingTrip.stock
);

export const getTodayClockInData = createSelector(
  getTripState,
  (state) => state.todayClockInData
);

export const getShowStockToolTip = createSelector(
  getTripState,
  (state) => state.showStockTootTip
);

export const getStockListWithQuantity = createSelector(
  getTripState,
  (state) => state.stockListWithQuantity
);
